import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { onNetworkRecovery } from "../lib/networkRecovery";

// How long to show the "Reconnecting..." indicator after the host
// (mobile resume, OS network reconnect) signals recovery, before
// assuming the GraphQL link is back to normal.
const RECONNECT_INDICATOR_MS = 2500;

export default function useOfflineMode() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Capacitor Network plugin (more reliable on native)
    let mounted = true;
    let removeCapacitorListener: (() => void) | undefined;
    if (Capacitor.isNativePlatform()) {
      import("@capacitor/network").then(({ Network }) => {
        if (!mounted) return;
        Network.getStatus().then((status) => {
          if (mounted) setIsOnline(status.connected);
        });
        const listenerPromise = Network.addListener("networkStatusChange", (status) => {
          if (mounted) setIsOnline(status.connected);
        });
        removeCapacitorListener = () => {
          listenerPromise.then((l) => l.remove());
        };
      }).catch(() => {});
    }

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const unsubRecover = onNetworkRecovery(() => {
      if (!mounted) return;
      setIsReconnecting(true);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => {
        if (mounted) setIsReconnecting(false);
      }, RECONNECT_INDICATOR_MS);
    });

    return () => {
      mounted = false;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      removeCapacitorListener?.();
      unsubRecover();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);

  return { isOnline, isOffline: !isOnline, isReconnecting };
}
